import React, { useCallback, useMemo } from 'react';
import {
  StatsigProvider as JotpsychStatsigProvider,
  StatsigSettingsContext,
  useFeatureGate,
  type StatsigClientInstance,
  type StatsigUser,
} from '@jotpsych/statsig';
import {
  createSanitizedAutoCapturePlugin,
  createTriggeredSessionReplayPlugin,
} from '@jotpsych/statsig/plugins';

import { APP_DEPLOY_VERSION } from '../../config/appVersion';
import { setNotificationStatsigClient } from '../../features/system-notifications/services/trackNotificationError';

import { setStatsigClientInstance } from './trackNoteTelemetryToStatsig';

interface StatsigProviderWrapperProps {
  children: React.ReactNode;
}

// `appVersion` is a recognized top-level StatsigUser field (see @statsig/client-core
// StatsigUser.d.ts). Keep it at root — not under `custom` — so gates and
// experiments that target the top-level field see the value.
const ANONYMOUS_USER: StatsigUser = {
  appVersion: APP_DEPLOY_VERSION,
};

/**
 * Inside the Statsig context: overrides the package's default `debugLogEvents` with
 * the web-app's `feature_show_analytics_logs` feature gate, so we can toggle
 * client-side analytics console output without a deploy.
 */
const DebugLogOverride: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const showAnalyticsLogs = useFeatureGate('feature_show_analytics_logs');
  const isDev = import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'dev';

  const settings = useMemo(
    () => ({ debugLogEvents: isDev || showAnalyticsLogs }),
    [isDev, showAnalyticsLogs],
  );

  return (
    <StatsigSettingsContext.Provider value={settings}>{children}</StatsigSettingsContext.Provider>
  );
};

export const StatsigProviderWrapper: React.FC<StatsigProviderWrapperProps> = ({ children }) => {
  const sdkKey = import.meta.env.VITE_STATSIG_SDK_KEY as string;
  const envTier = import.meta.env.VITE_BUILD_ENV === 'alpha' ? 'staging' : 'production';
  const enableLocalOverrides = Boolean(import.meta.env.DEV || import.meta.env.VITE_E2E_MODE);

  // Plugins must be constructed synchronously on first render — Statsig binds
  // them only during client construction and ignores later `options` changes.
  const plugins = useMemo(
    () => [createSanitizedAutoCapturePlugin(), createTriggeredSessionReplayPlugin()],
    [],
  );

  const handleClientReady = useCallback((client: StatsigClientInstance) => {
    setStatsigClientInstance(client);
    setNotificationStatsigClient(client);

    if (import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'dev') {
      console.debug('[Statsig] Client instance initialized for NoteTelemetry tracking');
    }
  }, []);

  return (
    <JotpsychStatsigProvider
      sdkKey={sdkKey}
      user={ANONYMOUS_USER}
      environment={envTier}
      plugins={plugins}
      enableLocalOverrides={enableLocalOverrides}
      onClientReady={handleClientReady}
    >
      <DebugLogOverride>{children}</DebugLogOverride>
    </JotpsychStatsigProvider>
  );
};
